import React, { useState, useEffect } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import { Container, Video, Word } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import "./SayASound.scss";
import icon from "./sayasound-icon.png";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";
import { ipaToAmirabet } from "../../../app/services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function SayASoundValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.say_a_sound;

  const { id } = useVersion(props.wordMetadata, {
    1: ["PHON", "GRAPHEMIC_BREAKDOWN", "PHONEME_VIDEO_URL"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: SayASound,
  }[versionId];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function SayASound(props) {
  const { word, wordMetadata, speech, StudentVideo } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, PHONEME_VIDEO_URL } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);

  const { speak, listenPhoneme, listenWord } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          const nSounds = graphemes.length;
          await cancellablePromise(speak(ttsStrings.intro({ nSounds })));
          await cancellablePromise(sleep());
          setPlaying(true);
          break;
        case 1:
          await cancellablePromise(speak(ttsStrings.ask_sounds));
          for (let i = 0; i < graphemes.length; i++) {
            setGraphemeIndex(i);
            const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
            if (status === "skip") return props.close();
          }
          setStage(2);
          break;
        case 2:
          await cancellablePromise(speak(ttsStrings.ask_word));
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onDonePlayingVideo(){
    if (!props.isMountedRef.current) return;
    try {
      await cancellablePromise(sleep());
      setStage(1);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return <Container icon={icon}>{renderContent()}</Container>;

  function renderContent() {
    if (stage === 0) {
      if(StudentVideo){
        return (
          <StudentVideo
            onDone={onDonePlayingVideo}
            videoURL={PHONEME_VIDEO_URL} 
            speech={speech}
            allowReplay={false}
            autoPlay={playing}
          />
        );
      }

      return (
        <Video
          playing={playing}
          src={PHONEME_VIDEO_URL}
          onEnded={onDonePlayingVideo}
        />
      )
    }

    if (stage === 1) {
      let grapheme = graphemes[graphemeIndex];
      if (graphemeIndex === -1) grapheme = " ";
      return (
        <>
          <div style={{ height: 150 }}>
            <Word>{grapheme}</Word>
          </div>
          <ListeningStatus />
        </>
      );
    }

    if (stage === 2) {
      return (
        <>
          <div style={{ height: 150 }}>
            {graphemes.map((g, i) => (
              <Word key={i} style={{ margin: "0 15px" }}>
                {g}
              </Word>
            ))}
          </div>
          <ListeningStatus />
        </>
      );
    }
  }
}
